<template>
  <div class="text-[#111111] flex items-center justify-center gap-2">
    <span class="text-[24px] font-bold">Карта коммерческих объектов</span>
  </div>

<div class="pb-1 bg-white rounded-[16px]">
    <!-- Header Section -->
    <div class="mb-6">
      <div class="relative flex w-full h-[720px]">
        <div class="absolute top-4 left-4 z-10 flex max-w-sm flex-col gap-3">
          <div class="flex flex-wrap gap-2">
            <button
              v-if="!isDrawing"
              class="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
              type="button"
              @click="startPolygonDrawing"
            >
              {{ polygons.length ? 'Добавить объект' : 'Начать создание объекта' }}
            </button>
            <button
              v-else
              class="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:bg-emerald-400"
              type="button"
              :disabled="!polygonIsReady"
              @click="finishPolygon"
            >
              Завершить добавление объекта
            </button>
            <button
              v-if="isDrawing || polygonPoints.length || isMetadataFormVisible"
              class="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
              type="button"
              @click="resetPolygon"
            >
              Сбросить
            </button>
          </div>
          <form
            v-if="isMetadataFormVisible"
            class="space-y-3 rounded-lg bg-white p-4 shadow-lg"
            @submit.prevent="savePolygonMeta"
          >
            <div>
              <label class="mb-1 block text-sm font-medium text-gray-700" for="polygon-title">
                Название
              </label>
              <input
                id="polygon-title"
                v-model.trim="newPolygonMeta.title"
                class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Например, Торговый центр"
                required
                type="text"
              />
            </div>
            <div>
              <label class="mb-1 block text-sm font-medium text-gray-700" for="polygon-description">
                Описание
              </label>
              <textarea
                id="polygon-description"
                v-model.trim="newPolygonMeta.description"
                class="h-20 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Краткая информация о зоне"
              ></textarea>
            </div>
            <div class="flex gap-3">
              <div class="flex-1">
                <label class="mb-1 block text-sm font-medium text-gray-700" for="polygon-fill">
                  Цвет заливки
                </label>
                <input
                  id="polygon-fill"
                  v-model="newPolygonMeta.fillColor"
                  class="h-9 w-full cursor-pointer rounded-md border border-gray-300"
                  type="color"
                />
              </div>
              <div class="flex-1">
                <label class="mb-1 block text-sm font-medium text-gray-700" for="polygon-stroke">
                  Цвет границы
                </label>
                <input
                  id="polygon-stroke"
                  v-model="newPolygonMeta.strokeColor"
                  class="h-9 w-full cursor-pointer rounded-md border border-gray-300"
                  type="color"
                />
              </div>
            </div>
            <div class="flex gap-2">
              <button
                class="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:bg-blue-400"
                type="submit"
                :disabled="!newPolygonDraft"
              >
                Сохранить
              </button>
              <button
                class="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
                type="button"
                @click="cancelPolygonMeta"
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
        <yandex-map
          class="flex-1"
          v-model="map"
          :settings="{
            location: {
              center: [76.952539, 43.218606],
              zoom: 13,
            },
          }"
        >
          <yandex-map-default-scheme-layer :settings="{
            layers: {
              ground: {
                zIndex: 1,
              },
              buildings: {
                zIndex: 2,
              },
              icons: {
                zIndex: -3,
              },
              labels: {
                zIndex: 3,
              },
            }
          }"/>
          <yandex-map-default-features-layer/>
          <yandex-map-hint hint-property="hint">
            <template #default="{ content }">
              <div class="rounded-md bg-white px-3 py-1 text-xs font-medium text-gray-800 shadow">
                {{ content }}
              </div>
            </template>
          </yandex-map-hint>
          <yandex-map-listener :settings="{ onClick: handleMapClick }" />
          <yandex-map-feature
            v-for="polygon in polygons"
            :key="polygon.id"
            :settings="getPolygonFeatureSettings(polygon)"
          />
          <yandex-map-feature
            v-if="isDrawing && draftLineCoordinates"
            :settings="{
              geometry: {
                type: 'LineString',
                coordinates: draftLineCoordinates,
              },
              style: draftLineStyle,
            }"
          />
          <yandex-map-feature
            v-if="polygonCoordinates"
            :settings="{
              geometry: {
                type: 'Polygon',
                coordinates: polygonCoordinates,
              },
              style: previewPolygonStyle,
            }"
          />
          <!-- <yandex-map-default-marker
            v-model="defaultMarker"
            :settings="{
              coordinates: defaultMarker ? defaultMarker.coordinates : [76.952539, 43.218606],
              title: `Долгота: ${ defaultMarker?.coordinates[0].toFixed(6) }<br>Широта: ${ defaultMarker?.coordinates[1].toFixed(6) }`,
              draggable: true,
              onDragMove,
            }"
          /> -->
          <yandex-map-controls :settings="{ position: 'left', orientation: 'vertical' }">
            <yandex-map-zoom-control/>
          </yandex-map-controls>
            <yandex-map-controls :settings="{ position: 'top right' }">
              <yandex-map-geolocation-control/>
            </yandex-map-controls>
            <yandex-map-controls :settings="{ position: 'bottom right', orientation: 'vertical' }">
              <yandex-map-open-maps-button/>
            </yandex-map-controls>
        </yandex-map>
      </div>

    </div>
</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, shallowRef, triggerRef, computed, watch } from 'vue';
import { useModalStore } from '@/store/modal';
import { useAuthStore } from '@/store/auth';
import { useToast } from '@/components/ui/toast';
import { useRoute, useRouter } from 'vue-router'
import { useSettingsStore } from '@/store/settings'
import {
  YandexMap,
  YandexMapDefaultSchemeLayer,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultMarker,
  YandexMapFeature,
  YandexMapHint,
  YandexMapListener,
  YandexMapControls,
  YandexMapGeolocationControl,
  YandexMapOpenMapsButton,
  YandexMapScaleControl,
  YandexMapZoomControl,
  YandexMapEntity
} from 'vue-yandex-maps';
import type { DomEventHandler, LngLat, LngLatBounds, YMap } from '@yandex/ymaps3-types';
import type { YMapDefaultMarker } from '@yandex/ymaps3-types/packages/markers';
import type { DrawingStyle } from '@yandex/ymaps3-types';

type PolygonMeta = {
  title: string;
  description: string;
  fillColor: string;
  strokeColor: string;
};

type PolygonItem = {
  id: string;
  coordinates: LngLat[][];
  meta: PolygonMeta;
  style: DrawingStyle;
};

type StoredPolygon = {
  id: string;
  coordinates: LngLat[][];
  meta: PolygonMeta;
};

const STORAGE_KEY = 'home.polygons';
const DEFAULT_CENTER: LngLat = [76.952539, 43.218606];
const CAMERA_FLIGHT_DURATION = 700;
const CAMERA_SETTLE_DURATION = 320;

const hexToRgba = (hex: string, alpha = 1): string => {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6 && normalized.length !== 3) {
    return hex;
  }

  const expand = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized;

  const r = parseInt(expand.slice(0, 2), 16);
  const g = parseInt(expand.slice(2, 4), 16);
  const b = parseInt(expand.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return hex;
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const createPolygonStyle = (fillColor: string, strokeColor: string): DrawingStyle => ({
  fill: hexToRgba(fillColor, 0.35),
  stroke: [{ color: strokeColor, width: 3 }],
  cursor: 'pointer',
  interactive: true,
});

const focusPolygon = async (polygon: PolygonItem) => {
  if (!map.value) {
    return;
  }

  const bounds = calculateBounds(polygon.coordinates);

  map.value.setCamera({
    tilt: degToRad(28),
    duration: CAMERA_FLIGHT_DURATION,
    easing: 'ease-in-out',
  });

  map.value.setLocation({
    bounds,
    duration: CAMERA_FLIGHT_DURATION,
    easing: 'ease-in-out',
  });

  await wait(CAMERA_FLIGHT_DURATION);

  // map.value.setCamera({
  //   tilt: 90,
  //   duration: CAMERA_SETTLE_DURATION,
  //   easing: 'ease-out',
  // });

  // await wait(CAMERA_SETTLE_DURATION);
};

const calculateBounds = (coordinates: LngLat[][]): LngLatBounds => {
  const points = coordinates.flatMap((ring) =>
    Array.isArray(ring)
      ? ring.filter(
          (point): point is LngLat =>
            Array.isArray(point) &&
            point.length === 2 &&
            point.every((value) => typeof value === 'number' && Number.isFinite(value)),
        )
      : [],
  );

  if (!points.length) {
    const [defaultLon, defaultLat] = DEFAULT_CENTER;
    return [
      [defaultLon - 0.01, defaultLat - 0.01],
      [defaultLon + 0.01, defaultLat + 0.01],
    ];
  }

  const longitudes = points.map(([lon]) => lon);
  const latitudes = points.map(([, lat]) => lat);
  const minLon = Math.min(...longitudes);
  const maxLon = Math.max(...longitudes);
  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);

  const lonPadding = Math.max((maxLon - minLon) * 0.15, 0.001);
  const latPadding = Math.max((maxLat - minLat) * 0.15, 0.001);

  return [
    [minLon - lonPadding, minLat - latPadding],
    [maxLon + lonPadding, maxLat + latPadding],
  ];
};

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const degToRad = (degrees: number) => (degrees * Math.PI) / 180;

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2, 11);
};

const settingsStore = useSettingsStore()
const route = useRoute()
const router = useRouter()
const isLoading = ref(false)
const modal = useModalStore()
const authStore = useAuthStore()
const { toast } = useToast()
const map = shallowRef<null | YMap>(null);
const defaultMarker = shallowRef<YMapDefaultMarker | null>(null);
const isDrawing = ref(false);
const polygonPoints = ref<LngLat[]>([]);
const polygons = ref<PolygonItem[]>([]);
const isMetadataFormVisible = ref(false);
const newPolygonDraft = ref<LngLat[][] | null>(null);

const defaultPolygonMeta: PolygonMeta = {
  title: '',
  description: '',
  fillColor: '#22c55e',
  strokeColor: '#15803d',
};
const newPolygonMeta = ref<PolygonMeta>({ ...defaultPolygonMeta });

const draftLineStyle: DrawingStyle = {
  stroke: [{ color: '#0ea5e9', width: 2 }],
};

const polygonIsReady = computed(() => polygonPoints.value.length >= 3);
const previewPolygonStyle = computed<DrawingStyle>(() => {
  const style = createPolygonStyle(newPolygonMeta.value.fillColor, newPolygonMeta.value.strokeColor);
  return { ...style, interactive: false, cursor: 'crosshair' };
});

const polygonCoordinates = computed(() => {
  if (!polygonIsReady.value) {
    return null;
  }

  const ring = polygonPoints.value.map(([lon, lat]) => [lon, lat] as LngLat);

  const [firstLon, firstLat] = ring[0];
  const [lastLon, lastLat] = ring[ring.length - 1];

  if (firstLon !== lastLon || firstLat !== lastLat) {
    ring.push([firstLon, firstLat] as LngLat);
  }

  return [ring];
});

const draftLineCoordinates = computed(() => {
  if (polygonPoints.value.length < 2) {
    return null;
  }
  return polygonPoints.value;
});

onMounted(async () => {
  restorePolygons();
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', handleBeforeUnload);
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  }
});

const onDragMove = () => {
    triggerRef(defaultMarker);
};

const resetDraftState = () => {
  polygonPoints.value = [];
  newPolygonDraft.value = null;
  isMetadataFormVisible.value = false;
  newPolygonMeta.value = { ...defaultPolygonMeta };
  isDrawing.value = false;
};

const startPolygonDrawing = () => {
  resetDraftState();
  isDrawing.value = true;
};

const finishPolygon = () => {
  if (!polygonIsReady.value) {
    toast({
      title: 'Недостаточно точек',
      description: 'Добавьте как минимум три точки, чтобы завершить полигон.',
      variant: 'destructive',
    });
    return;
  }

  const coordinates = polygonCoordinates.value;
  if (!coordinates) {
    return;
  }

  newPolygonDraft.value = coordinates;
  isDrawing.value = false;
  isMetadataFormVisible.value = true;
};

const resetPolygon = () => {
  resetDraftState();
};

const savePolygonMeta = () => {
  if (!newPolygonDraft.value) {
    return;
  }

  const meta = { ...newPolygonMeta.value };
  const polygon: PolygonItem = {
    id: generateId(),
    coordinates: newPolygonDraft.value,
    meta,
    style: createPolygonStyle(meta.fillColor, meta.strokeColor),
  };

  polygons.value = [...polygons.value, polygon];
  resetDraftState();
};

const cancelPolygonMeta = () => {
  resetDraftState();
};

const handleMapClick: DomEventHandler = (object, event) => {
  if (!isDrawing.value || isMetadataFormVisible.value) {
    return;
  }

  const coords = event.coordinates as LngLat;
  polygonPoints.value = [...polygonPoints.value, [coords[0], coords[1]]];
};

const handleBeforeUnload = () => {
  persistPolygons();
};

const formatPolygonTitle = (polygon: PolygonItem) =>
  polygon.meta.title?.trim() || 'Полигон без названия';

const handlePolygonClick = async (polygon: PolygonItem) => {
  const title = formatPolygonTitle(polygon);
  const description = polygon.meta.description?.trim();
  const message = description ? `${title}\nОписание: ${description}` : title;
  await focusPolygon(polygon);
  modal.openModal('ShowBuildingModal', { polygon });
};

const getPolygonFeatureSettings = (polygon: PolygonItem) => ({
  geometry: {
    type: 'Polygon' as const,
    coordinates: polygon.coordinates,
  },
  style: polygon.style,
  properties: {
    title: formatPolygonTitle(polygon),
    hint: formatPolygonTitle(polygon),
    description: polygon.meta.description,
  },
  onClick: () => handlePolygonClick(polygon),
});

const persistPolygons = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const payload: StoredPolygon[] = polygons.value.map(({ id, coordinates, meta }) => ({
    id,
    coordinates,
    meta,
  }));

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
};

const restorePolygons = () => {
  if (typeof window === 'undefined') {
    return;
  }

  let raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    // return;
    raw = '[{"id":"2ee0d1c1-69fa-478c-88f7-fe59d0c315c7","coordinates":[[[76.98816137660613,43.285388825601466],[76.9880139562345,43.28568706426902],[76.9880933364346,43.286747456494204],[76.98807065637743,43.286979414811896],[76.98813869654897,43.287426760480365],[76.9904293823239,43.28992023766573],[76.99177884572596,43.28859481410512],[76.99251594758422,43.28743504462816],[76.99292418861342,43.286308390096764],[76.98885311835005,43.28523142126657],[76.9885015774638,43.28523970571547],[76.98816137660613,43.285388825601466]]],"meta":{"title":"Halyk Arena","description":"Halyk Arena","fillColor":"#22c55e","strokeColor":"#15803d"}},{"id":"91e989fc-456f-4372-9c15-26d2617f0cb7","coordinates":[[[76.9815363799586,43.278677408195506],[76.98259546311124,43.27740405899648],[76.98341323617849,43.27760975568452],[76.98384223188589,43.27804073695061],[76.98238096525755,43.279098587024066],[76.9815363799586,43.278677408195506]]],"meta":{"title":"Adil","description":"hata","fillColor":"#5a21c4","strokeColor":"#3a157f"}},{"id":"399bae2c-d10f-4b48-b219-979e041f5cca","coordinates":[[[76.97612191506096,43.29049723781337],[76.97748326027718,43.293041605724476],[76.98151485151791,43.29128060609122],[76.98155088855412,43.28863715230888],[76.97856492769469,43.28833159562096],[76.97612191506096,43.29049723781337]]],"meta":{"title":"Балуан Ш","description":"Балуан Ш","fillColor":"#176c96","strokeColor":"#5e1e7b"}}]';
  }

  try {
    const restored = JSON.parse(raw) as StoredPolygon[];
    polygons.value = restored.map((item) => {
      const meta: PolygonMeta = {
        title: item.meta?.title ?? defaultPolygonMeta.title,
        description: item.meta?.description ?? defaultPolygonMeta.description,
        fillColor: item.meta?.fillColor ?? defaultPolygonMeta.fillColor,
        strokeColor: item.meta?.strokeColor ?? defaultPolygonMeta.strokeColor,
      };

      return {
        id: item.id,
        coordinates: item.coordinates,
        meta,
        style: createPolygonStyle(meta.fillColor, meta.strokeColor),
      };
    });
  } catch (error) {
    console.error('Failed to restore polygons from localStorage', error);
  }
};

watch(
  polygons,
  () => {
    persistPolygons();
  },
  { deep: true },
);
</script>

<style scoped>
</style>
